import ClientAgentExtender from '../Common/clientAgentExtender'
import OrderDocument from './OrderDocument'
import {StoritevEnum} from '../Enum/StoritevEnum'
import OrderService from './OrderService'

export default class Order extends ClientAgentExtender {
  /**
   * @type {[OrderService]}
   * @private
   */
  _services = []
  /**
   * @type {number | null}
   * @private
   */
  _id = null
  /**
   * @type {[OrderDocument]}
   * @private
   */
  _documents = []
  /**
   * @type {boolean | null}
   * @private
   */
  _hasEmail = null
  /**
   * @type {boolean | null}
   * @private
   */
  _hasGsm = null
  /**
   * @type {boolean | null}
   * @private
   */
  _immediatelyActivated = null
  /**
   * @type {string | null}
   * @private
   */
  _serviceName = null
  /**
   * @type {boolean | null}
   * @private
   */
  _isSubscription = null
  /**
   * @type {number}
   * @private
   */
  _defaultTimeout = 60000
  /**
   * @type {number}
   * @private
   */
  _recaptchaVersion = 3

  constructor () {
    super()
    this._services = []
  }

  /**
   * @param {OrderService} orderService
   */
  addService (orderService) {
    this._services.push(orderService)
  }

  addAdditionalDataToServiceWithId (idService, additionalDataKey, additionalDataValue) {
    this._services.forEach((s) => {
      if (s.serviceId === idService) {
        s.addOrEditAditionalData(additionalDataKey, additionalDataValue)
      }
    })
  }

  async create () {
    if (this._services.length === 0) {
      throw new Error('Napaka pri kreiranju naročila')
    }
    let request = {services: JSON.stringify(this.getServicesRequest())}
    let response = await this.ca.makeRequest('order/create', request, 'POST', this._recaptchaVersion, this._defaultTimeout)
    this._id = response.id
    this._isSubscription = response.orderChange
    let documentsRequest = {
      id: this._id,
      documentExtension: 'pdf'
    }
    try {
      let docsResponse = await this.ca.makeRequest('order/getDocuments', documentsRequest, 'GET', this._recaptchaVersion, this._defaultTimeout)
      let docs = []
      docsResponse.documents.forEach((doc) => {
        docs.push(OrderDocument.createFromResponseItem(doc))
      })
      this._documents = docs
    } catch (e) {
      await this.cancel()
      throw new Error(e.message)
    }
  }

  async signDocumentsAndComplete () {
    if (this._id === null) {
      throw new Error('Napaka pri potrjevanju naročila')
    }
    let request = {id: this._id, services: JSON.stringify(this.getServicesRequest())}
    let response = await this.ca.makeRequest('order/signDocumentsAndComplete', request, 'POST', this._recaptchaVersion, this._defaultTimeout)
    this._hasEmail = response.mailNotification
    this._hasGsm = response.gsmNotification
    this._immediatelyActivated = response.serviceEnabled
    this._isSubscription = response.orderChange
  }

  async cancel () {
    if (this._id === null) {
      throw new Error('Napaka pri stornaciji naročila')
    }
    let request = {id: this._id}
    await this.ca.makeRequest('order/cancel', request, 'POST')
    this._id = null
  }

  getFinalNotificationTitle () {
    let out = ''
    if (this._isSubscription) {
      out = 'Potrjujemo spremembo naročenih storitev'
      if (!this._immediatelyActivated) {
        if (
          this.hasServiceWithId(StoritevEnum.PANDA_DOME_ESSENTIAL) ||
          this.hasServiceWithId(StoritevEnum.PANDA_DOME_COMPLETE)
        ) {
          out = 'Neuspešno naročilo'
        }
        if (this.hasT2AppService()) {
          out = 'Neuspešna aktivacija T-2 TV'
        }
      }
    } else {
      out = 'Potrjujemo odjavo storitve "' + this._serviceName + '"'
    }
    return out
  }

  getFinalNotificationText () {
    let out = ''
    if (this._isSubscription) {
      if (this._immediatelyActivated) {
        if (this._hasEmail) {
          out = 'Potrdilo o sklenjeni pogodbi smo posredovali na kontaktni elektronski naslov naročnika in odložili na ' +
            ' portal Moj T-2, v zavihek Dokumenti, kjer si ga lahko ogledate in shranite v .pdf obliki.'
        } else {
          out = 'Potrdilo o sklenjeni pogodbi smo odložili na portal MOJ T-2, v zavihek Dokumenti, kjer ' +
            'si ga lahko ogledate in shranite v .pdf obliki.'
        }
      } else {
        if (this._hasEmail) {
          out = 'Potrdilo o sklenjeni pogodbi smo posredovali na kontaktni elektronski naslov naročnika in ' +
            'odložili na portal Moj T-2, v zavihek Dokumenti, kjer si ga lahko ogledate in shranite v .pdf ' +
            'obliki. Ob vklopu naročenih storitev vas bomo obvestili preko SMS sporočila.'
        } else {
          out = 'Potrdilo o sklenjeni pogodbi smo odložili na portal MOJ T-2, v zavihek Dokumenti, kjer ' +
            'si ga lahko ogledate in shranite v .pdf obliki. Ob vklopu naročenih storitev vas bomo obvestili ' +
            'preko SMS sporočila.'
        }
        if (this.hasT2AppService()) {
          out = 'Oprostite, pri obdelavi vašega naročila je prišlo do napake, zato aktivacija T-2 TV trenutno ni možna.' +
            '<br><br>' +
            'Prosimo, pokličite nas na 064 064 064, kjer vam bo naš svetovalec pomagal urediti aktivacijo T-2 TV.'
        }
        if (
          this.hasServiceWithId(StoritevEnum.PANDA_DOME_ESSENTIAL) ||
          this.hasServiceWithId(StoritevEnum.PANDA_DOME_COMPLETE)
        ) {
          out = 'Oprostite, pri obdelavi vašega naročila je prišlo do napake.' +
            '<br><br>' +
            'Prosimo, pokličite nas na 064 064 064, kjer vam bo naš svetovalec pomagal pri naročilu.'
        }
      }
    } else {
      if (this._hasEmail) {
        out = 'Potrdilo o spremembi naročenih storitev smo posredovali na kontaktni elektronski naslov naročnika ter ' +
          'odložili na portal Moj T-2, v zavihek Dokumenti, kjer si ga boste lahko ogledali in shranili v .pdf obliki.'
      } else {
        out = 'Obvestilo o spremembi naročenih storitev smo poslali preko SMS sporočila in odložili potrdilo o ' +
          'spremembi naročenih storitev na portal MOJ T-2, v zavihek Dokumenti, kjer si ga lahko ogledate in ' +
          'shranite v .pdf obliki.'
      }
    }
    return out
  }

  getServicesRequest () {
    let out = []
    this._services.forEach((item) => {
      out.push(item.getRequestItem())
    })
    return out
  }

  hasT2AppService () {
    return this.hasServiceWithId(StoritevEnum.APLIKACIJA_T2_TV)
  }

  hasServiceWithId (id) {
    let out = false
    for (let i = 0; i < this._services.length; i++) {
      if (this._services[i].serviceId === id) {
        out = true
        break
      }
    }
    return out
  }

  getServiceById (id) {
    let out = null
    for (let i = 0; i < this._services.length; i++) {
      if (this._services[i].serviceId === id) {
        out = this._services[i]
        break
      }
    }
    return out
  }

  getT2AppService () {
    return this.getServiceById(StoritevEnum.APLIKACIJA_T2_TV)
  }

  parseServicesFromArrayOfObjects (s) {
    s.forEach((service) => {
      let orderService = new OrderService(
        service.serviceId,
        service.quantity,
        service.terminationPointId || null,
        service.customerId || null
      )
      if (Array.isArray(service.additionalData) && service.additionalData.length > 0) {
        service.additionalData.forEach((item) => {
          orderService.addOrEditAditionalData(item.key, item.value)
        })
      }
      this.addService(orderService)
    })
  }

  get id () {
    return this._id
  }

  set id (value) {
    this._id = value
  }

  get documents () {
    return this._documents
  }

  set documents (value) {
    this._documents = value
  }

  get services () {
    return this._services
  }

  set services (value) {
    this._services = value
  }

  get hasEmail () {
    return this._hasEmail
  }

  set hasEmail (value) {
    this._hasEmail = value
  }

  get hasGsm () {
    return this._hasGsm
  }

  set hasGsm (value) {
    this._hasGsm = value
  }

  get immediatelyActivated () {
    return this._immediatelyActivated
  }

  set immediatelyActivated (value) {
    this._immediatelyActivated = value
  }

  get serviceName () {
    return this._serviceName
  }

  set serviceName (value) {
    this._serviceName = value
  }

  get isSubscription () {
    return this._isSubscription
  }

  set isSubscription (value) {
    this._isSubscription = value
  }
}



// WEBPACK FOOTER //
// ./src/assets/js/Order/Order.js